import { cn } from "@/lib/cn";
import { AuthenticatedImage } from "@/components/forms/AuthenticatedImage";
import { initials } from "@/lib/format";

export interface AvatarProps {
  name: string;
  fileId?: string | null;
  size?: "sm" | "md" | "lg";
  /** Sits on the dark board surface rather than a light one. */
  onDark?: boolean;
  className?: string;
}

const SIZES = {
  sm: "size-8 text-2xs",
  md: "size-10 text-xs",
  lg: "size-14 text-md",
};

/**
 * Grounds for the initials of somebody with no photograph: a solid colour
 * with white letters. In a column of twenty the hue
 * only separates one row from the next. Each is a step white clears 4.5:1
 * on, and none is the red or the amber that mean something elsewhere.
 */
const GROUNDS = [
  "bg-avatar-blue text-white",
  "bg-avatar-violet text-white",
  "bg-avatar-green text-white",
  "bg-avatar-teal text-white",
  "bg-avatar-coral text-white",
  "bg-avatar-grey text-white",
] as const;

/** Same name, same ground, every time and on every screen. */
function ground(name: string): string {
  let hash = 0;
  for (let index = 0; index < name.length; index += 1) {
    hash = (hash * 31 + name.charCodeAt(index)) | 0;
  }
  return GROUNDS[Math.abs(hash) % GROUNDS.length]!;
}

/**
 * Photo if there is one, initials if not. Never an anonymous silhouette.
 *
 * Circular, because it is the one thing on the page that is a person rather
 * than a record, and the only shape in the system that is not a rounded square.
 */
export function Avatar({ name, fileId, size = "md", onDark = false, className }: AvatarProps) {
  const base = cn(
    "grid shrink-0 place-items-center overflow-hidden rounded-full font-semibold",
    SIZES[size],
    onDark ? "bg-board-soft text-board-text" : ground(name),
    className,
  );

  return (
    <AuthenticatedImage
      fileId={fileId}
      alt=""
      className={cn(base, "object-cover")}
      fallback={
        <span className={base} aria-hidden="true">
          {initials(name)}
        </span>
      }
    />
  );
}
